import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Shield, FileText, CheckCircle, AlertCircle, Download, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

function ClientDossierView() {
  const [searchParams] = useSearchParams();
  const dossierId = searchParams.get('id');
  const accessToken = searchParams.get('token');
  
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revisionText, setRevisionText] = useState('');
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [revisionSubmitted, setRevisionSubmitted] = useState(false);

  useEffect(() => {
    if (dossierId && accessToken) {
      fetchDossier();
    }
  }, [dossierId, accessToken]);

  const fetchDossier = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/dossiers/${dossierId}?token=${accessToken}`);
      
      if (!response.ok) {
        throw new Error('Accès refusé');
      }
      
      const data = await response.json();
      setDossier(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dossier:', error);
      toast.error('Impossible de charger votre dossier. Vérifiez le lien.');
      setLoading(false);
    }
  };

  const handleRevisionSubmit = async () => {
    if (!revisionText.trim()) {
      toast.error('Veuillez décrire les modifications souhaitées');
      return;
    }

    setSubmittingRevision(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/dossiers/${dossierId}/revision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          additional_info: revisionText,
          client_email: dossier.client_info?.email || '',
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la soumission');
      }

      toast.success('Demande de révision envoyée avec succès !');
      setRevisionSubmitted(true);
      setRevisionText('');
    } catch (error) {
      console.error('Error submitting revision:', error);
      toast.error('Erreur lors de l\'envoi de votre demande. Réessayez.');
    } finally {
      setSubmittingRevision(false);
    }
  };

  const handleDownloadPDF = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backendUrl}/api/dossiers/${dossierId}/pdf?token=${accessToken}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de votre dossier...</p>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Dossier introuvable</h2>
          <p className="text-muted-foreground text-sm">
            Le lien d'accès est invalide ou expiré. Vérifiez l'email que vous avez reçu.
          </p>
        </Card>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { label: 'Brouillon', color: 'bg-gray-500' },
      'paid': { label: 'Payé', color: 'bg-blue-500' },
      'processing': { label: 'En traitement', color: 'bg-amber-500' },
      'ready': { label: 'Prêt', color: 'bg-green-500' },
      'delivered': { label: 'Livré', color: 'bg-green-600' },
    };
    const s = statusMap[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${s.color} text-white`}>{s.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="container-narrow py-4">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-accent" />
            <h1 className="text-xl font-semibold text-primary">Votre Dossier</h1>
          </div>
        </div>
      </header>

      <div className="py-8 sm:py-12">
        <div className="container-narrow max-w-4xl">
          {/* En-tête dossier */}
          <Card className="p-6 mb-6" data-testid="dossier-header">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Dossier #{dossier._id?.slice(-8) || 'N/A'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Type : {dossier.problem_type || 'Non spécifié'}
                </p>
              </div>
              {getStatusBadge(dossier.status)}
            </div>
            
            {dossier.client_info && (
              <>
                <Separator className="my-4" />
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Nom :</span>
                    <span className="ml-2 font-medium">{dossier.client_info.prenom} {dossier.client_info.nom}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email :</span>
                    <span className="ml-2 font-medium">{dossier.client_info.email}</span>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* Lettre générée */}
          {dossier.letter_draft && dossier.status === 'delivered' && (
            <Card className="p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Votre courrier</h3>
                </div>
                <Button
                  size="sm"
                  onClick={handleDownloadPDF}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="download-pdf-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </div>
              
              <div className="bg-white border border-border rounded-lg p-6 max-h-[500px] overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm font-mono text-foreground">
                  {dossier.letter_draft}
                </pre>
              </div>

              <div className="mt-4 flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Courrier livré</p>
                  <p className="text-xs text-green-700">
                    Vous pouvez télécharger ce courrier en PDF et l'envoyer aux autorités compétentes.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Demande de révision */}
          {dossier.status === 'delivered' && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-lg">Demander une modification</h3>
              </div>
              
              {!revisionSubmitted ? (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    Si vous souhaitez apporter des modifications à votre courrier, décrivez ci-dessous 
                    les changements souhaités ou les informations complémentaires à ajouter.
                  </p>
                  
                  <Textarea
                    value={revisionText}
                    onChange={(e) => setRevisionText(e.target.value)}
                    placeholder="Exemple : J'ai oublié de mentionner que j'ai envoyé un recommandé le 15 janvier. Pouvez-vous ajouter cette information dans le courrier ?"
                    rows={6}
                    className="mb-4"
                    data-testid="revision-textarea"
                  />
                  
                  <Button
                    onClick={handleRevisionSubmit}
                    disabled={submittingRevision}
                    className="bg-accent hover:bg-accent/90 text-white"
                    data-testid="submit-revision-button"
                  >
                    {submittingRevision ? 'Envoi en cours...' : 'Envoyer la demande'}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground mt-3">
                    Notre équipe examinera votre demande et vous enverra une version révisée sous 24-48h.
                  </p>
                </>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">Demande envoyée avec succès</p>
                      <p className="text-sm text-green-700 mt-1">
                        Nous avons bien reçu votre demande de modification. 
                        Vous recevrez une réponse par email sous 24-48h.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Disclaimer */}
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-900">
              <strong>Assistance rédactionnelle uniquement</strong> — Ce service ne constitue pas un conseil juridique. 
              Les courriers générés ne sont pas révisés par des avocats. 
              Pour toute question juridique complexe, veuillez consulter un professionnel du droit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientDossierView;
